// i18next bootstrap. Lazy-loads locale JSON for 7 languages with the user's
// preference resolved in this order: explicit set() call → localStorage →
// browser → "en". Saving to localStorage is automatic; persistence to the
// user profile happens in LanguageSwitcher.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import ht from './locales/ht.json';
import zh from './locales/zh.json';
import vi from './locales/vi.json';
import ar from './locales/ar.json';

export const LANGUAGES = [
  { code: 'en', label: 'English',         native: 'English',     flag: '🇺🇸', rtl: false },
  { code: 'es', label: 'Spanish',         native: 'Español',     flag: '🇪🇸', rtl: false },
  { code: 'fr', label: 'French',          native: 'Français',    flag: '🇫🇷', rtl: false },
  { code: 'ht', label: 'Haitian Creole',  native: 'Kreyòl',      flag: '🇭🇹', rtl: false },
  { code: 'zh', label: 'Chinese (Simp.)', native: '简体中文',     flag: '🇨🇳', rtl: false },
  { code: 'vi', label: 'Vietnamese',      native: 'Tiếng Việt',  flag: '🇻🇳', rtl: false },
  { code: 'ar', label: 'Arabic',          native: 'العربية',     flag: '🇸🇦', rtl: true },
];

export const SUPPORTED_LANG_CODES = LANGUAGES.map(l => l.code);

const STORAGE_KEY = 'savenes_language';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      fr: { translation: fr },
      ht: { translation: ht },
      zh: { translation: zh },
      vi: { translation: vi },
      ar: { translation: ar },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANG_CODES,
    interpolation: { escapeValue: false },
    // CRITICAL: disable Suspense — without a wrapping <Suspense> boundary,
    // every component using useTranslation() will render *nothing* while
    // i18n is loading, which produces an instant blank screen on every
    // authenticated route. With suspense:false, useTranslation returns
    // immediately and falls back to keys if resources aren't ready yet.
    react: { useSuspense: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
  });

// Whenever the language changes, flip the <html> dir/lang attributes so
// Arabic flows right-to-left across the entire app.
function applyDirection(lng) {
  const lang = LANGUAGES.find(l => l.code === lng) || LANGUAGES[0];
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang.code;
    document.documentElement.dir = lang.rtl ? 'rtl' : 'ltr';
  }
}
applyDirection(i18n.language || 'en');
i18n.on('languageChanged', applyDirection);

export default i18n;
